import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, User, Check, Edit } from 'lucide-react';
import { useState, useMemo, useEffect } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Loader } from 'src/components/Loader';
import { CustomerEditSheet } from 'src/components/customers/CustomerEditSheet';

export type AutocompleteItem = {
  id: string | number;
  name: string;
  email: string;
  friendlyId?: string;
  type?: 'individual' | 'contractor';
  userId?: string;
};

type Props = {
  items: AutocompleteItem[];
  selectedItem?: AutocompleteItem;
  onSelect: (item: AutocompleteItem) => void;
  searchPlaceholder?: string;
  className?: string;
  maxDisplayItems?: number;
  isLoading?: boolean;
  refetchCustomers?: () => void;
  onSearch?: (search: string) => void;
  searchValue?: string;
};

// Utility function to generate initials
const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
};

// Utility function to generate avatar color
const getAvatarColor = (name: string): string => {
  const colors = [
    'bg-gradient-to-br from-blue-500 to-purple-600',
    'bg-gradient-to-br from-pink-500 to-red-500',
    'bg-gradient-to-br from-cyan-500 to-blue-500',
    'bg-gradient-to-br from-green-500 to-teal-500',
    'bg-gradient-to-br from-purple-500 to-pink-500',
    'bg-gradient-to-br from-indigo-500 to-purple-500',
    'bg-gradient-to-br from-orange-500 to-red-500',
    'bg-gradient-to-br from-teal-500 to-green-500',
  ];

  return colors[name.length % colors.length];
};

const CustomerAvatar = ({ name }: { name: string }) => {
  const initials = getInitials(name);
  const colorClass = getAvatarColor(name);

  return (
    <div
      className={`w-10 h-10 rounded-full ${colorClass} flex items-center justify-center text-white font-semibold text-sm flex-shrink-0`}
    >
      {initials}
    </div>
  );
};

const CustomerItem = ({
  item,
  isSelected,
  onSelect,
  onEdit,
  isLoading = false,
  isLast = false,
}: {
  item: AutocompleteItem;
  isSelected: boolean;
  onSelect: (item: AutocompleteItem) => void;
  onEdit: (item: AutocompleteItem) => void;
  isLast?: boolean;
  isLoading?: boolean;
}) => {
  const [isSelecting, setIsSelecting] = useState(false);
  const handleSelect = (event: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
    event.stopPropagation();
    setIsSelecting(true);
    onSelect(item);
  };

  useEffect(() => {
    if (!isLoading) {
      setIsSelecting(false);
    }
  }, [isLoading]);

  const getBadgeVariant = (type?: string) => {
    switch (type) {
      case 'contractor':
        return 'informational' as const;
      case 'individual':
        return 'secondary' as const;
      default:
        return 'secondary' as const;
    }
  };

  const getDisplayType = (type?: string) => {
    switch (type) {
      case 'contractor':
        return 'Contractor';
      case 'individual':
        return 'Residential';
      default:
        return 'Residential';
    }
  };

  return (
    <div
      className={`flex items-center gap-3 p-4 cursor-pointer transition-all ${
        isSelected ? 'bg-gray-100 hover:bg-gray-200' : 'hover:bg-gray-50'
      } ${!isLast ? 'border-b border-border' : ''}`}
      onClick={handleSelect}
    >
      <CustomerAvatar name={item.name} />

      <div className="flex-1 min-w-0">
        <div className="font-semibold text-text-primary text-base truncate">{item.name}</div>
        <div className="text-muted text-sm truncate">{item.email}</div>
        {item.friendlyId && (
          <div className="text-muted/80 text-xs mt-1">
            ID: {item.friendlyId} •{' '}
            <Badge variant={getBadgeVariant(item.type)}>{getDisplayType(item.type)}</Badge>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <Button
          variant="ghost"
          className="hover:bg-secondary"
          size="sm"
          onClick={(event) => {
            event.stopPropagation();
            onEdit(item);
          }}
          disabled={isLoading}
        >
          <Edit className="h-4 w-4" />
        </Button>
        {isLoading && (isSelected || isSelecting) ? (
          <Loader />
        ) : isSelected ? (
          <div className="w-5 h-5 m-2 bg-primary rounded-full flex items-center justify-center">
            <Check className="w-3 h-3 text-white" />
          </div>
        ) : (
          <div className="w-5 h-5 p-4 hover:bg-secondary rounded flex items-center justify-center">
            <Checkbox />
          </div>
        )}
      </div>
    </div>
  );
};

export const Autocomplete = ({
  items,
  selectedItem,
  onSelect,
  searchPlaceholder = 'Search by name, email, or ID...',
  className,
  maxDisplayItems = 20,
  isLoading = false,
  refetchCustomers,
  onSearch,
  searchValue,
}: Props) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [editingCustomer, setEditingCustomer] = useState<AutocompleteItem | null>(null);

  // Use controlled searchValue if provided, otherwise use internal state
  const currentSearchValue = searchValue !== undefined ? searchValue : searchQuery;

  const displayItems = useMemo(() => {
    // If onSearch is provided, server-side filtering is being used
    if (onSearch) {
      return items;
    }

    // Otherwise, use client-side filtering
    if (currentSearchValue.trim()) {
      const query = currentSearchValue.toLowerCase();
      return items.filter(
        (item) =>
          item.name.toLowerCase().includes(query) ||
          item.email.toLowerCase().includes(query) ||
          (item.friendlyId && item.friendlyId.toString().includes(query)),
      );
    } else {
      return items.slice(0, maxDisplayItems);
    }
  }, [items, currentSearchValue, maxDisplayItems, onSearch]);

  const handleSelect = (item: AutocompleteItem) => {
    onSelect(item);
  };

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Search Section */}
      <div className="sticky top-0 z-10 border-b border-border p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder={searchPlaceholder}
            value={currentSearchValue}
            onChange={(e) => {
              const value = e.target.value;
              // Only update internal state if not controlled
              if (searchValue === undefined) {
                setSearchQuery(value);
              }
              onSearch?.(value);
            }}
            className="pl-10"
          />
        </div>
      </div>

      {/* Customer List */}
      <div className="flex-1 overflow-auto">
        {displayItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <User className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-lg font-medium">No customers found</p>
            <p className="text-sm">Try adjusting your search terms</p>
          </div>
        ) : (
          <div>
            {displayItems.map((item, index) => (
              <CustomerItem
                key={item.id}
                item={item}
                isSelected={selectedItem?.id === item.id}
                onSelect={handleSelect}
                onEdit={setEditingCustomer}
                isLoading={isLoading}
                isLast={index === displayItems.length - 1}
              />
            ))}
          </div>
        )}
      </div>

      {/* Customer Edit Sheet */}
      {editingCustomer && editingCustomer.userId && (
        <CustomerEditSheet
          isOpen
          onClose={() => setEditingCustomer(null)}
          customerId={editingCustomer.id.toString()}
          refetchCustomers={() => {
            refetchCustomers?.();
            setEditingCustomer(null);
          }}
        />
      )}
    </div>
  );
};
